import type { ReactNode } from 'react';
import { Arc, Trace } from 'loading-dev';
import styles from './Loader.module.css';

export type LoaderProps = {
  label?: ReactNode;
  size?: 'sm' | 'md' | 'lg';
  /** `arc` = default busy. `trace` = brand/full-page. `mark` = monogram breathe. */
  variant?: 'arc' | 'trace' | 'mark';
  tone?: 'default' | 'onBrand';
};

const SIZE_PX = {
  sm: 16,
  md: 20,
  lg: 28,
} as const;

const DURATION_MS = {
  arc: 1200,
  trace: 1800,
} as const;

export function Loader({ label, size = 'md', variant = 'arc', tone = 'default' }: LoaderProps) {
  const sizeClass = styles[`size_${size}`];
  const toneClass = tone === 'onBrand' ? styles.tone_onBrand : styles.tone_default;
  const wrapClass = variant === 'mark' && label ? styles.wrap_mark : undefined;
  const px = SIZE_PX[size];

  if (variant === 'mark') {
    return (
      <span className={`${styles.wrap} ${wrapClass ?? ''}`.trim()} role="status" aria-live="polite">
        <span className={`${styles.mark} ${sizeClass}`} aria-hidden>
          <span className={`${styles.monogram} ${toneClass}`} />
        </span>
        {label ? <span>{label}</span> : null}
      </span>
    );
  }

  const Spinner = variant === 'trace' ? Trace : Arc;
  const duration = variant === 'trace' ? DURATION_MS.trace : DURATION_MS.arc;

  return (
    <span
      className={`${styles.wrap} ${toneClass}`.trim()}
      role="status"
      aria-live="polite"
    >
      <span className={`${styles.spinner} ${sizeClass}`} aria-hidden>
        <Spinner size={px} duration={duration} cap="round" />
      </span>
      {label ? <span>{label}</span> : null}
    </span>
  );
}
